import { useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type Country = components["schemas"]["CountryBankProfileSummary"];

interface CountryPickerProps {
  value: string;
  onChange: (value: string, country: Country | null) => void;
  countries?: Country[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
}

export function CountryPicker({
  value,
  onChange,
  countries: providedCountries,
  placeholder = "Select a country...",
  disabled,
  id,
}: CountryPickerProps) {
  const [open, setOpen] = useState(false);
  const [fetched, setFetched] = useState<Country[]>([]);
  const fetchStarted = useRef(false);

  useEffect(() => {
    if (providedCountries || fetchStarted.current) return;
    fetchStarted.current = true;
    async function fetchCountries() {
      const { data } = await api.GET("/v1/bank-details/countries");
      if (data) setFetched(data);
    }
    fetchCountries();
  }, [providedCountries]);

  const countries = providedCountries ?? fetched;
  const selected = countries.find(
    (c) => c.countryCode.toUpperCase() === value.toUpperCase(),
  );

  // modal: these pickers sit inside Dialogs, whose scroll lock swallows wheel events on the portaled list.
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          {selected ? (
            <span className="truncate">
              {selected.countryName} ({selected.countryCode})
            </span>
          ) : value ? (
            <span className="truncate">{value}</span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] p-0"
      >
        <Command>
          <CommandInput placeholder="Search countries..." />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {countries.map((country) => (
                <CommandItem
                  key={country.countryCode}
                  value={`${country.countryCode} ${country.countryName}`}
                  onSelect={() => {
                    onChange(country.countryCode.toUpperCase(), country);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn(
                      "mr-2 size-4",
                      selected?.countryCode === country.countryCode
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  {country.countryName}
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    ({country.countryCode})
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
